import React, { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { supabase } from '../../supabaseClient';
import FileUpload from '@/components/ui/fileupload';

const AgencyDetails = () => {
  const [, navigate] = useLocation();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({ name: '', phone: '', email: '', commissionPercentage: '' });
  const [match, params] = useRoute('/agency-details/:agencyId');
  

  const agencyId = params?.agencyId; 

  useEffect(() => {
    const fetchAgencyDetails = async () => {
      if (!agencyId) {
        setError('Agency ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const { data: agencyData, error: agencyError } = await supabase
          .from('agency')
          .select('*')
          .eq('id', agencyId)
          .single();

        if (agencyError) {
          setError(agencyError.message);
        } else {
          setAgency(agencyData);
          setFormValues({
            name: agencyData.name,
            phone: agencyData.phone,
            email: agencyData.email,
            commissionPercentage: agencyData.commissionPercentage,
          });
        }
      } catch (err) {
        setError('Error fetching agency details.');
      } finally {
        setLoading(false);
      }
    };

    fetchAgencyDetails();
  }, [agencyId]);
  const handleBack = () => window.history.back();
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleUpdate = async () => {
    try {
      const updatedValues = {
        name: formValues.name,
        phone: formValues.phone,
        email: formValues.email,
        commissionPercentage: formValues.commissionPercentage ? parseFloat(formValues.commissionPercentage) : null,
      };
  
      const { data, error } = await supabase
        .from('agency')
        .update(updatedValues)
        .eq('id', agencyId)
        .select(); 
  
      if (error) {
        setError(error.message);
      } else {
        setAgency(data[0]); 
        setDialogOpen(false);
      }
    } catch (err) {
      setError('Error updating agency details.');
    }
  };
  
  

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!agency) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No agency details found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Agency Details
        </Typography>
        <Box>
          <Button
            variant="contained"
            onClick={handleDialogOpen}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
              mr: 1,
            }}
          >
            Update
          </Button>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {agency?.name}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Phone:
              </Typography>
              <Typography variant="body1">{agency.phone}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Email:
              </Typography>
              <Typography variant="body1">{agency.email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Commission Percentage:
              </Typography>
              <Typography variant="body1">{agency.commissionPercentage}</Typography>
            </Grid>
          </Grid>
          <FileUpload basepath={'agency/'+agency.id+'/'}/>
        </CardContent>
      </Card>

      {/* Update Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Update Agency Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            fullWidth
            margin="normal"
            value={formValues.name}
            onChange={handleInputChange}
          />
          <TextField
            label="Phone"
            name="phone"
            fullWidth
            margin="normal"
            value={formValues.phone}
            onChange={handleInputChange}
          />
          <TextField
            label="Email"
            name="email"
            fullWidth
            margin="normal"
            value={formValues.email}
            onChange={handleInputChange}
          />
          <TextField
            label="Commission Percentage"
            name="commissionPercentage"
            fullWidth
            margin="normal"
            value={formValues.commissionPercentage}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary"
           sx={{
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: '#333',
            },
            mr: 1,
          }}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary" variant="contained"
           sx={{
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: '#333',
            },
            mr: 1,
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgencyDetails;
